<template>
  <div v-if="dispatchDate">
    <div class="body-2">
      {{ i18n(translations.title) }}
    </div>
    <div class="body-2-bold" data-test="expected-dispatch-date">
      <RevTag
        v-if="tag"
        data-allow-mismatch
        :label="tag.label"
        :variant="tag.color"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag, type Variants } from '@ds/components/Tag'

import { SHORT_DATE_FORMAT } from '~/constants/dateTimeFormats'
import { getDifferenceInDays } from '~/utils/date/getDifferenceInDays'
import { getLocalDate } from '~/utils/date/getLocalDate'

import type { Order } from '../../../../../../Tab.types'

import { DISAMBIGUATED_MARKET_TIMEZONES } from './ExpectedDispatchDate.constants'
import translations from './ExpectedDispatchDate.translations'

const i18n = useI18n()

const props = defineProps<{
  market: Order['market']
  currentDate?: Order['shipping']['expectedDispatchDate']
  dispatchDate?: Order['shipping']['expectedDispatchDate']
}>()

const tag = computed(() => {
  if (!props.dispatchDate) {
    return null
  }
  // US and Australia have multiple timezones, but the date is calculated on a unique timezone country-wide.
  // We need to map the date to its original timezone to avoid the date being shifted by one when displaying.
  const timeZone = DISAMBIGUATED_MARKET_TIMEZONES[props.market]
  const currentDate = getLocalDate(props.currentDate, timeZone)
  const expectedDate = getLocalDate(props.dispatchDate, timeZone)
  const differenceInDays = getDifferenceInDays(currentDate, expectedDate)

  if (differenceInDays < 0) {
    return {
      color: 'danger' as Variants,
      label: i18n(translations.overdue),
    }
  }

  if (differenceInDays === 0) {
    return {
      color: 'primary' as Variants,
      label: i18n(translations.today),
    }
  }

  if (differenceInDays === 1) {
    return {
      color: 'success' as Variants,
      label: i18n(translations.tomorrow),
    }
  }

  return {
    color: 'secondary' as Variants,
    label: i18n.date(expectedDate, SHORT_DATE_FORMAT),
  }
})
</script>
